/**
 * Client-side engagement tracking for the /pilot landing page.
 *
 * Generates and persists a visitor_id (localStorage + first-party cookie),
 * parses Apollo / utm URL params on init, buffers engagement events, and
 * flushes them in batches to the trackPilotEvents mutation. Uses
 * navigator.sendBeacon on pagehide so we don't lose the closing event.
 */

import {ApolloClient, gql} from '@apollo/client';
import {
  readCurrentApolloParams,
  type ApolloVisitorParams,
} from './pilotApolloParams';

const VISITOR_STORAGE_KEY = 'pilot_visitor_id';
const SESSION_STORAGE_KEY = 'pilot_session_id';
const COOKIE_VISITOR_KEY = 'pe_pilot_vid';
const COOKIE_MAX_AGE_DAYS = 365;
const BUFFER_FLUSH_THRESHOLD = 5;
const SCROLL_DEPTH_MARKS = [25, 50, 75, 100];

export const TRACK_PILOT_EVENTS = gql`
  mutation TrackPilotEvents(
    $visitorId: ID
    $sessionId: ID
    $identify: PilotVisitorIdentifyInput
    $events: [PilotEventInput!]!
  ) {
    trackPilotEvents(
      visitorId: $visitorId
      sessionId: $sessionId
      identify: $identify
      events: $events
    ) {
      ok
      visitorId
      sessionId
      accepted
    }
  }
`;

interface BufferedEvent {
  name: string;
  payload?: Record<string, unknown>;
  occurredAt: string;
}

interface PilotTrackingState {
  visitorId: string;
  sessionId?: string;
  apolloParams: ApolloVisitorParams;
  buffer: BufferedEvent[];
  scrollDepthFired: Set<number>;
  sectionsFired: Set<string>;
  client?: ApolloClient<unknown>;
  identified: boolean;
  destroyed: boolean;
  // Listener refs captured at setup so we can detach them in
  // teardownPilotTracking — otherwise navigating away from and back
  // to /#/pilot leaks duplicate handlers and inflates scroll_depth /
  // pagehide counts on every remount.
  onScroll?: () => void;
  onVisibilityChange?: () => void;
  onPageHide?: () => void;
}

let state: PilotTrackingState | null = null;

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return 'pe-' + Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\\/+^]/g, '\\$&') + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? '; Secure'
    : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

function loadVisitorId(): string {
  if (typeof window === 'undefined') return uuid();
  try {
    const fromStorage = window.localStorage.getItem(VISITOR_STORAGE_KEY);
    if (fromStorage) {
      writeCookie(COOKIE_VISITOR_KEY, fromStorage);
      return fromStorage;
    }
  } catch {
    /* localStorage may throw in private mode */
  }
  const fromCookie = readCookie(COOKIE_VISITOR_KEY);
  if (fromCookie) {
    try {
      window.localStorage.setItem(VISITOR_STORAGE_KEY, fromCookie);
    } catch {
      /* ignore */
    }
    return fromCookie;
  }
  const fresh = uuid();
  try {
    window.localStorage.setItem(VISITOR_STORAGE_KEY, fresh);
  } catch {
    /* ignore */
  }
  writeCookie(COOKIE_VISITOR_KEY, fresh);
  return fresh;
}

function persistSessionId(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

function loadSessionId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.sessionStorage.getItem(SESSION_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function buildIdentifyVariables(s: PilotTrackingState) {
  const {apolloParams} = s;
  return {
    email: apolloParams.identity.email,
    firstName: apolloParams.identity.firstName,
    lastName: apolloParams.identity.lastName,
    company: apolloParams.identity.company,
    title: apolloParams.identity.title,
    linkedinUrl: apolloParams.identity.linkedinUrl,
    phone: apolloParams.identity.phone,
    apolloContactId: apolloParams.apolloContactId,
    apolloCampaignId: apolloParams.apolloCampaignId,
    utmSource: apolloParams.utm.source,
    utmMedium: apolloParams.utm.medium,
    utmCampaign: apolloParams.utm.campaign,
    utmContent: apolloParams.utm.content,
    utmTerm: apolloParams.utm.term,
    rawParams: apolloParams.rawParams,
    referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };
}

async function flushBuffered(opts: {includeIdentify: boolean} = {includeIdentify: false}): Promise<void> {
  if (!state || state.destroyed) return;
  if (state.buffer.length === 0 && !opts.includeIdentify) return;
  if (!state.client) return;

  const events = state.buffer.splice(0, state.buffer.length);
  const identify =
    opts.includeIdentify && !state.identified
      ? buildIdentifyVariables(state)
      : opts.includeIdentify
        ? buildIdentifyVariables(state)
        : undefined;

  try {
    const result = await state.client.mutate({
      mutation: TRACK_PILOT_EVENTS,
      variables: {
        visitorId: state.visitorId,
        sessionId: state.sessionId,
        identify,
        events,
      },
      fetchPolicy: 'no-cache',
    });
    const data = result.data?.trackPilotEvents;
    if (data?.sessionId && state) {
      state.sessionId = data.sessionId;
      persistSessionId(data.sessionId);
      state.identified = true;
    }
    if (data?.visitorId && state && state.visitorId !== data.visitorId) {
      state.visitorId = data.visitorId;
      try {
        window.localStorage.setItem(VISITOR_STORAGE_KEY, data.visitorId);
      } catch {
        /* ignore */
      }
      writeCookie(COOKIE_VISITOR_KEY, data.visitorId);
    }
  } catch (err) {
    if (events.length > 0 && state) {
      // Re-buffer if the call failed and we still have state.
      state.buffer.unshift(...events);
    }
    if (typeof console !== 'undefined') {
      console.warn('[pilotTracking] flush failed:', err);
    }
  }
}

function flushViaBeacon(): void {
  if (!state || state.destroyed) return;
  if (!('sendBeacon' in navigator)) return;
  if (state.buffer.length === 0) return;
  const events = state.buffer.splice(0, state.buffer.length);

  // We use the same GraphQL endpoint as Apollo. For sendBeacon we can't
  // include the Bearer header — but this endpoint is fully public so
  // that's fine.
  const endpoint = inferGraphqlEndpoint();
  if (!endpoint) return;

  const body = JSON.stringify({
    query: TRACK_PILOT_EVENTS.loc?.source.body,
    variables: {
      visitorId: state.visitorId,
      sessionId: state.sessionId,
      events,
    },
  });
  try {
    const blob = new Blob([body], {type: 'application/json'});
    navigator.sendBeacon(endpoint, blob);
  } catch {
    /* ignore */
  }
}

function inferGraphqlEndpoint(): string | undefined {
  let env: string | undefined;
  try {
    env =
      typeof process !== 'undefined'
        ? (process.env as Record<string, string | undefined>)?.REACT_APP_GRAPHQL_URL
        : undefined;
  } catch {
    env = undefined;
  }
  if (env) return env;
  if (typeof window === 'undefined') return undefined;
  return `${window.location.origin}/graphql`;
}

function setupScrollDepth(s: PilotTrackingState): void {
  if (typeof window === 'undefined') return;
  const onScroll = () => {
    if (!state || state.destroyed) return;
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop;
    const height = doc.scrollHeight - doc.clientHeight;
    if (height <= 0) return;
    const pct = Math.round((scrollTop / height) * 100);
    for (const mark of SCROLL_DEPTH_MARKS) {
      if (pct >= mark && !state.scrollDepthFired.has(mark)) {
        state.scrollDepthFired.add(mark);
        trackEvent('scroll_depth', {percent: mark});
      }
    }
  };
  s.onScroll = onScroll;
  window.addEventListener('scroll', onScroll, {passive: true});
}

function setupVisibilityHandlers(s: PilotTrackingState): void {
  if (typeof document === 'undefined') return;
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      flushViaBeacon();
    }
  };
  const onPageHide = () => {
    flushViaBeacon();
  };
  s.onVisibilityChange = onVisibilityChange;
  s.onPageHide = onPageHide;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
}

export function initPilotTracking(client: ApolloClient<unknown>): void {
  if (state && !state.destroyed) {
    state.client = client;
    return;
  }
  if (typeof window === 'undefined') return;
  const apolloParams = readCurrentApolloParams();
  state = {
    visitorId: loadVisitorId(),
    sessionId: loadSessionId(),
    apolloParams,
    buffer: [],
    scrollDepthFired: new Set(),
    sectionsFired: new Set(),
    client,
    identified: false,
    destroyed: false,
  };

  // Fire the initial page_view + identify immediately.
  const path =
    typeof window !== 'undefined' && window.location
      ? window.location.pathname + window.location.hash
      : '/pilot';
  trackEvent('page_view', {
    path,
    hasIdentity: apolloParams.hasAnyIdentity,
    hasAttribution: apolloParams.hasAnyAttribution,
  });
  void flushBuffered({includeIdentify: true});

  setupScrollDepth(state);
  setupVisibilityHandlers(state);
}

export function trackEvent(
  name: string,
  payload?: Record<string, unknown>,
): void {
  if (!state || state.destroyed) return;
  state.buffer.push({
    name,
    payload,
    occurredAt: new Date().toISOString(),
  });
  if (state.buffer.length >= BUFFER_FLUSH_THRESHOLD) {
    void flushBuffered();
  }
}

export function observeSectionVisibility(
  sectionId: string,
  el: Element | null,
): () => void {
  if (!el || typeof IntersectionObserver === 'undefined') return () => {};
  let timer: ReturnType<typeof setTimeout> | null = null;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (!timer) {
            timer = setTimeout(() => {
              if (state && !state.sectionsFired.has(sectionId)) {
                state.sectionsFired.add(sectionId);
                trackEvent('section_view', {sectionId});
              }
              timer = null;
            }, 1000);
          }
        } else if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      }
    },
    {threshold: [0, 0.5, 1]},
  );
  observer.observe(el);
  return () => {
    observer.disconnect();
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
}

export function getVisitorId(): string | undefined {
  return state?.visitorId;
}

export function getApolloParams(): ApolloVisitorParams | undefined {
  return state?.apolloParams;
}

export function flushPilotTracking(): Promise<void> {
  return flushBuffered();
}

export function teardownPilotTracking(): void {
  if (!state) return;
  void flushBuffered();
  // Detach the listeners we registered in initPilotTracking — leaving
  // them attached would let stale state-less closures fire on the next
  // mount (and pile up after several mounts) since the React landing
  // page registers fresh listeners every time it mounts.
  if (typeof window !== 'undefined') {
    if (state.onScroll) {
      window.removeEventListener('scroll', state.onScroll);
    }
    if (state.onPageHide) {
      window.removeEventListener('pagehide', state.onPageHide);
    }
  }
  if (typeof document !== 'undefined' && state.onVisibilityChange) {
    document.removeEventListener('visibilitychange', state.onVisibilityChange);
  }
  state.destroyed = true;
  state = null;
}
